// LF-007-RETURN-FALLBACK (2026-02-24, founder-locked): the primary Stripe
// success flow now returns customers straight to /visualiser/p/{id}. This
// page is kept as a lightweight fallback for legacy receipt links only.
// Founder rules:
//   • Keep the route; do not remove.
//   • Payment-confirmed message must be clear.
//   • Primary CTA: "Open my projects".
//   • If a project reference is already present in the URL, offer a link
//     to that project — but NEVER guess or infer one from unreliable data.
//   • No additional redesign or refactor.
import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { CheckCircle2, ArrowRight } from "lucide-react";

export default function UnlockSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const projectFromUrl = params.get("project");
  const nav = useNavigate();

  // Best-effort silent confirmation so a legacy Stripe receipt link still
  // triggers the atomic unlock on the backend if it hasn't happened yet.
  // We never block the UI on this — the customer's already paid and is
  // owed a clear confirmation immediately.
  const [resolvedProjectId, setResolvedProjectId] = useState(projectFromUrl);
  useEffect(() => {
    if (!sessionId) return;
    let dead = false;
    (async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (dead) return;
        // Only trust the backend-attached project id — do NOT guess.
        if (data?.project_id) setResolvedProjectId(data.project_id);
      } catch { /* silent — the fallback message stands on its own */ }
    })();
    return () => { dead = true; };
  }, [sessionId]);

  return (
    <div className="max-w-lg mx-auto px-5 py-16 sm:py-24 text-center" data-testid="unlock-success-page">
      <CheckCircle2 className="mx-auto text-brand-teal mb-5" size={56} data-testid="unlock-success-icon" />
      <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-2" data-testid="unlock-success-title">
        Payment confirmed
      </h1>
      <p className="text-sm text-brand-muted mb-8" data-testid="unlock-success-body">
        Your project is unlocked. Open it whenever you&rsquo;re ready to render.
      </p>

      <div className="flex flex-col items-center gap-3">
        <button
          onClick={() => nav("/projects")}
          data-testid="unlock-success-projects-btn"
          className="inline-flex items-center gap-2 bg-brand-navy text-white h-12 px-6 rounded-2xl text-sm font-semibold hover:bg-brand-navy/90 hover:shadow-navy transition-all"
        >
          Open my projects <ArrowRight size={15} />
        </button>

        {resolvedProjectId && (
          <button
            onClick={() => nav(`/visualiser/p/${resolvedProjectId}`)}
            data-testid="unlock-success-open-project-btn"
            className="text-xs text-brand-muted hover:text-brand-ink underline underline-offset-2"
          >
            Or jump straight into this project
          </button>
        )}
      </div>
    </div>
  );
}
